import { _adapters } from 'chart.js';
import dayjs, { ManipulateType, QUnitType } from 'dayjs';
import AdvancedFormat from 'dayjs/plugin/advancedFormat.js';
import CustomParseFormat from 'dayjs/plugin/customParseFormat.js';
import isoWeek from 'dayjs/plugin/isoWeek.js';
import LocalizedFormat from 'dayjs/plugin/localizedFormat.js';
import QuarterOfYear from 'dayjs/plugin/quarterOfYear.js';
import type { TimeUnit } from 'chart.js';

dayjs.extend(AdvancedFormat);
dayjs.extend(QuarterOfYear);
dayjs.extend(LocalizedFormat);
dayjs.extend(CustomParseFormat);
dayjs.extend(isoWeek);

const FORMATS = {
  datetime: 'MMM D, YYYY, h:mm:ss a',
  day: 'MMM D',
  hour: 'hA',
  millisecond: 'h:mm:ss.SSS a',
  minute: 'h:mm a',
  month: 'MMM YYYY',
  quarter: '[Q]Q - YYYY',
  second: 'h:mm:ss a',
  week: 'll',
  year: 'YYYY'
};

type ChartDateValue = string | number | Date | dayjs.Dayjs;
type ChartUnit = TimeUnit | QUnitType;

_adapters._date.override({
  add: (time: ChartDateValue, amount: number, unit: ChartUnit) =>
    dayjs(time)
      .add(amount, unit as ManipulateType)
      .valueOf(),
  diff: (max: ChartDateValue, min: ChartDateValue, unit: TimeUnit) =>
    dayjs(max).diff(dayjs(min), unit),
  endOf: (time: ChartDateValue, unit: ChartUnit) => dayjs(time).endOf(unit).valueOf(),
  format: (time: ChartDateValue, format: string) => dayjs(time).format(format),
  formats: () => FORMATS,
  parse: (value?: ChartDateValue | null, format?: string) => {
    if (value === null || typeof value === 'undefined') return null;
    if (typeof value === 'string' && typeof format === 'string') {
      const parsedValue = dayjs(value, format);
      return parsedValue.isValid() ? parsedValue.valueOf() : null;
    }
    const parsedValue = dayjs(value);
    return parsedValue.isValid() ? parsedValue.valueOf() : null;
  },
  startOf: (time: ChartDateValue, unit: ChartUnit | 'isoWeek', weekday?: number) => {
    if (unit === 'isoWeek') {
      const validatedWeekday =
        typeof weekday === 'number' && weekday >= 1 && weekday <= 7 ? weekday : 1;
      return dayjs(time).isoWeekday(validatedWeekday).startOf('day').valueOf();
    }
    return dayjs(time).startOf(unit).valueOf();
  }
});
